/**
 * Results of the two pre-expiry emails, on the Renewals page.
 *
 * One table per email, one row per batch (memberships ending the same day)
 * per title plus an all-titles row. Every count opens the members behind it.
 * The 30-day notice reports renewals by any channel; the 7-day reminder
 * reports what happened to the charge on the renewal date.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { Button } from '../components/Button'
import { fmtDate, fmtInt } from '../lib/format'

type Kind = 'expiry' | 'reminder'
type Counts = Record<string, number>
type BatchRow = {
  period_end: string
  publication_slug: string | null
  publication_name: string
  counts: Counts
  /** Notice series step (30/15/5/0); null on the All emails row and reminder rows. */
  step: number | null
}
type Results = { as_of: string; rows: BatchRow[] }
type MemberRow = {
  subscriber_id: number
  member_name: string
  account_number: string | null
  email: string
  publication_slug: string
  period_end: string
  email_status: string | null
  outcome: string
  detail: string | null
  step: number | null
}
type Pick = { kind: Kind; row: BatchRow; bucket: string; label: string }

const stepLabel = (n: number | null) => (n === null ? 'All emails' : n === 0 ? 'End date' : `${n} days`)
const day = (iso: string) => fmtDate(iso, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
const short = (iso: string) => fmtDate(iso, { month: 'short', day: 'numeric', timeZone: 'UTC' })
const daysBetween = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000)

const OUTCOME: Record<string, string> = {
  renewed: 'Renewed', not_renewed: 'Not renewed', charged: 'Charged', card_failed: 'Card failed',
  turned_off: 'Turned off', renewed_other: 'Renewed another way', other: 'Other', not_due: 'Not due yet',
}

type Col = { bucket: string; label: string; pct?: boolean; tone?: 'good' | 'bad' }

const ENGAGEMENT: Col[] = [
  { bucket: 'sent', label: 'Sent' },
  { bucket: 'delivered', label: 'Delivered', pct: true },
  { bucket: 'opened', label: 'Opened', pct: true },
  { bucket: 'clicked', label: 'Clicked', pct: true },
]
const EXPIRY_COLS: Col[] = [
  ...ENGAGEMENT,
  { bucket: 'renewed', label: 'Renewed', pct: true, tone: 'good' },
  { bucket: 'clicked_renewed', label: 'Clicked + renewed', pct: true, tone: 'good' },
  { bucket: 'not_renewed', label: 'Not renewed yet', pct: true },
]
const REMINDER_COLS: Col[] = [
  ...ENGAGEMENT,
  { bucket: 'charged', label: 'Charged', pct: true, tone: 'good' },
  { bucket: 'card_failed', label: 'Card failed', pct: true, tone: 'bad' },
  { bucket: 'turned_off', label: 'Turned off', pct: true },
  { bucket: 'renewed_other', label: 'Renewed another way', pct: true, tone: 'good' },
  { bucket: 'other', label: 'Other', pct: true },
]

export function RenewalEmailResults() {
  const [pick, setPick] = useState<Pick | null>(null)
  return (
    <section className="space-y-6">
      <div>
        <h2 className="label">Renewal email results</h2>
        <p className="mt-1 text-sm text-paper-dim">
          What happened to each batch of pre-expiry emails, and how many members renewed. Click a number to see the members.
        </p>
      </div>
      <ResultsTable
        kind="expiry"
        title="Expiry notices"
        who="Auto-renew off · 30, 15, 5 days before and on the end date"
        note="Renewed counts anyone whose membership now runs past the end date, however they paid. On each step it counts the members for whom that email was the last one before they renewed; the All emails row counts each member once."
        cols={EXPIRY_COLS}
        onPick={setPick}
      />
      <ResultsTable
        kind="reminder"
        title="7-day renewal reminder"
        who="Annual · auto-renew on · card on file"
        note="These members renew automatically, so the outcome is what happened to the charge on the renewal date."
        cols={REMINDER_COLS}
        onPick={setPick}
      />
      <p className="text-[12px] text-paper-faint max-w-[90ch]">
        Opened runs high because Apple Mail opens every email automatically; Clicked is the more reliable signal.
      </p>
      {pick && <MembersModal pick={pick} onClose={() => setPick(null)} />}
    </section>
  )
}

function ResultsTable({ kind, title, who, note, cols, onPick }: {
  kind: Kind; title: string; who: string; note: string; cols: Col[]; onPick: (p: Pick) => void
}) {
  const [older, setOlder] = useState(false)
  const batches = older ? 24 : 6
  const q = useQuery({
    queryKey: ['renewal-email-results', kind, batches],
    queryFn: () => api.get<Results>(`/api/renewals/expiry-notices/results?kind=${kind}&batches=${batches}`),
  })
  const rows = q.data?.rows ?? []
  const asOf = q.data?.as_of ?? ''
  const periods = [...new Set(rows.map((r) => r.period_end))]
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-serif text-[22px] text-paper">{title}</h3>
        <span className="label">{who}</span>
      </div>
      <p className="text-sm text-paper-dim max-w-[80ch]">{note}</p>
      {q.isLoading && <p className="label">Loading…</p>}
      {q.isError && <p className="text-radar-red font-mono text-[12px]">Couldn&rsquo;t load the results.</p>}
      {q.data && rows.length === 0 && <p className="text-sm text-paper-dim">Nothing has been sent yet.</p>}
      {rows.length > 0 && (
        <div className="overflow-x-auto border border-ink-500">
          <table className="w-full text-sm tabular-nums">
            <thead>
              <tr className="border-b border-ink-500">
                <th className="label px-3 py-2 text-left">{kind === 'expiry' ? 'Ends' : 'Renews'}</th>
                {kind === 'expiry' && <th className="label px-3 py-2 text-left">Email</th>}
                <th className="label px-3 py-2 text-left">Title</th>
                {cols.map((c) => <th key={c.bucket} className="label px-3 py-2 text-right whitespace-nowrap">{c.label}</th>)}
                <th className="label px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => {
                const ended = daysBetween(asOf, p) <= 0
                return [
                  <tr key={`h-${p}`}>
                    <td colSpan={cols.length + (kind === 'expiry' ? 4 : 3)} className="label-amber px-3 pt-4 pb-1">
                      {kind === 'expiry' ? 'Memberships ending' : 'Renewing'} {day(p)}
                    </td>
                  </tr>,
                  ...rows.filter((r) => r.period_end === p).map((r) => (
                    <tr key={`${p}-${r.step ?? 'all'}-${r.publication_slug ?? 'all'}`}
                        className={`border-b border-ink-700/40 ${r.publication_slug ? '' : 'bg-amber-wash font-semibold'} ${
                          kind === 'expiry' && r.step === null ? 'border-t-2 border-t-amber-glow/40' : ''}`}>
                      <td className="px-3 py-2 font-mono text-[12px]">{short(p)}</td>
                      {kind === 'expiry' && (
                        <td className="px-3 py-2 font-mono text-[12px] whitespace-nowrap">{stepLabel(r.step)}</td>
                      )}
                      <td className="px-3 py-2 whitespace-nowrap">
                        {kind === 'expiry' && r.step === null ? 'All titles' : r.publication_name}
                      </td>
                      {cols.map((c) => (
                        <CountCell key={c.bucket} kind={kind} row={r} col={c} ended={ended} onPick={onPick} />
                      ))}
                      <td className="px-3 py-2 whitespace-nowrap"><StatusPill kind={kind} row={r} asOf={asOf} /></td>
                    </tr>
                  )),
                ]
              })}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && !older && periods.length >= 6 && (
        <Button variant="ghost" size="sm" onClick={() => setOlder(true)}>Show older batches</Button>
      )}
    </div>
  )
}

function CountCell({ kind, row, col, ended, onPick }: {
  kind: Kind; row: BatchRow; col: Col; ended: boolean; onPick: (p: Pick) => void
}) {
  const n = row.counts[col.bucket] ?? 0
  const sent = row.counts.sent ?? 0
  const lapsed = col.bucket === 'not_renewed' && ended
  const label = lapsed ? 'Lapsed' : col.label
  const tone = lapsed || (col.tone === 'bad' && n > 0) ? 'text-radar-red'
    : col.tone === 'good' && n > 0 ? 'text-radar-green' : 'text-paper'
  const awaiting = col.bucket === 'delivered' ? row.counts.awaiting ?? 0 : 0
  return (
    <td className="px-3 py-2 text-right align-top whitespace-nowrap">
      {n === 0 ? (
        <span className="font-mono text-paper-faint">0</span>
      ) : (
        <button type="button" onClick={() => onPick({ kind, row, bucket: col.bucket, label })}
                className={`font-mono hover:text-amber-glow hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-glow ${tone}`}>
          {fmtInt(n)}
        </button>
      )}
      {lapsed && n > 0 && <span className="block text-[10px] font-mono text-radar-red">lapsed</span>}
      {col.pct && sent > 0 && (
        <span className="block text-[10.5px] font-mono text-paper-faint">{Math.round((100 * n) / sent)}%</span>
      )}
      {awaiting > 0 && (
        <button type="button" onClick={() => onPick({ kind, row, bucket: 'awaiting', label: 'Awaiting report' })}
                className="block ml-auto text-[10.5px] font-mono text-paper-faint hover:text-amber-glow">
          {fmtInt(awaiting)} awaiting report
        </button>
      )}
    </td>
  )
}

function StatusPill({ kind, row, asOf }: { kind: Kind; row: BatchRow; asOf: string }) {
  const left = daysBetween(asOf, row.period_end)
  let text: string
  let wait = false
  if (row.counts.sent === 0) text = 'none due'
  else if (left > 0) {
    wait = true
    text = kind === 'expiry' ? `${left} day${left === 1 ? '' : 's'} left` : `charges ${short(row.period_end)}`
  } else text = kind === 'expiry' ? 'ended' : 'done'
  return (
    <span className={`font-mono text-[10px] px-2 py-0.5 border ${wait ? 'border-amber-glow text-amber-glow' : 'border-ink-500 text-paper-dim'}`}>
      {text}
    </span>
  )
}

function MembersModal({ pick, onClose }: { pick: Pick; onClose: () => void }) {
  const { kind, row, bucket, label } = pick
  const params = new URLSearchParams({ kind, period_end: row.period_end, bucket })
  if (row.publication_slug) params.set('publication_slug', row.publication_slug)
  if (row.step !== null) params.set('step', String(row.step))
  const q = useQuery({
    queryKey: ['renewal-email-members', params.toString()],
    queryFn: () => api.get<{ rows: MemberRow[] }>(`/api/renewals/expiry-notices/results/members?${params}`),
  })
  return (
    <div className="fixed inset-0 bg-ink-900/80 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <div className="bg-ink-800 border border-ink-500 w-full max-w-[960px] max-h-[85vh] flex flex-col"
           onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-ink-500">
          <div>
            <p className="label">
              {kind === 'expiry' ? `Expiry notice · ${stepLabel(row.step)}` : '7-day renewal reminder'} ·{' '}
              {kind === 'expiry' && row.step === null ? 'All titles' : row.publication_name} · {day(row.period_end)}
            </p>
            <h3 className="font-serif text-[22px] text-paper mt-1">{label}{q.data ? ` · ${fmtInt(q.data.rows.length)}` : ''}</h3>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="overflow-auto p-5">
          {q.isLoading && <p className="label">Loading…</p>}
          {q.isError && <p className="text-radar-red font-mono text-[12px]">Couldn&rsquo;t load the members.</p>}
          {q.data && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-500 text-left">
                  <th className="label px-3 py-2">Member</th>
                  <th className="label px-3 py-2">Account</th>
                  <th className="label px-3 py-2">Email</th>
                  {!row.publication_slug && <th className="label px-3 py-2">Title</th>}
                  <th className="label px-3 py-2">Email status</th>
                  <th className="label px-3 py-2">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((m) => (
                  <tr key={`${m.subscriber_id}-${m.publication_slug}`} className="border-b border-ink-700/40 align-top">
                    <td className="px-3 py-2">
                      <Link to={`/subscribers/${m.subscriber_id}`} className="text-paper hover:text-amber-glow hover:underline">
                        {m.member_name || `#${m.subscriber_id}`}
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-[12px] text-paper-dim">{m.account_number ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-[12px] text-paper-dim">{m.email}</td>
                    {!row.publication_slug && <td className="px-3 py-2 text-[12px]">{m.publication_slug}</td>}
                    <td className="px-3 py-2 font-mono text-[12px]">{m.email_status ?? 'not sent'}</td>
                    <td className="px-3 py-2 text-[12px]">
                      {OUTCOME[m.outcome] ?? m.outcome}
                      {m.detail && <span className="block text-paper-faint font-mono text-[11px]">{m.detail}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
